import { notify } from '@/components/core';
import { memo, useState } from 'react';
import { apiFetch } from '@/api';
import { Handle, Position, useNodes, useEdges } from 'reactflow';
import { Button } from '@/components/ui/button';
import { BarChart, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DataProfileReport } from './DataProfileReport';

export const DataProfileNode = memo(({ data, id, isConnectable, selected }: any) => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<any>(null);
    const [open, setOpen] = useState(false);

    // Get current graph state
    const nodes = useNodes();
    const edges = useEdges();

    const handleRunProfile = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent node selection
        setLoading(true);
        try {
            // Construct Graph Object matching Backend.Domain.Flows.FlowGraph
            const flowGraph = {
                Nodes: nodes,
                Edges: edges,
                Variables: [] // Optional if needed
            };

            // Use apiFetch with null token to let it use the centralized getStoredToken() logic
            const result = await apiFetch('/api/dataprofile/analyze', null, {
                method: 'POST',
                body: JSON.stringify({
                    NodeId: id,
                    FlowGraph: flowGraph
                })
            });

            if (!result) throw new Error("Failed to analyze");
            setReportData(result);
            setOpen(true);
        } catch (e) {
            console.error("Data Profile Node Error:", e);
            notify.error("Profile Failed: " + (e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className={cn(
                "relative rounded-xl border bg-white shadow-sm min-w-[240px] transition-all duration-200",
                selected ? "border-indigo-500 ring-2 ring-indigo-500/20" : "border-indigo-100 hover:border-indigo-300"
            )}
        >
            {/* HANDLES */}

            {/* Target Handles (Input) - Left, Top, Bottom */}
            <Handle
                type="target"
                position={Position.Left}
                id="in"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-indigo-500 border-2 border-white z-50"
            />
            <Handle
                type="target"
                position={Position.Top}
                id="in-top"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-indigo-500 border-2 border-white z-50 opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <Handle
                type="target"
                position={Position.Bottom}
                id="in-bottom"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-indigo-500 border-2 border-white z-50 opacity-0 group-hover:opacity-100 transition-opacity"
            />

            <div className="flex items-center gap-3 p-4 border-b border-indigo-50/50 bg-indigo-50/10 rounded-t-xl">
                <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0 border border-indigo-200">
                    <BarChart className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-gray-900 leading-tight">Data Profile</h3>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mt-0.5">Analyze Health & Stats</p>
                </div>
            </div>

            {/* Execution Status Badge */}
            {(data?.executionStatus === 'RUNNING' || data?.executionStatus === 'Running') && (
                <div className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md z-20 border border-slate-200">
                    <Loader2 className="h-3.5 w-3.5 text-blue-600 animate-spin" strokeWidth={2.5} />
                </div>
            )}
            {(data?.executionStatus === 'COMPLETED' || data?.executionStatus === 'Success') && (
                <div className="absolute -top-2 -right-2 p-1 bg-green-600 rounded-full shadow-md z-20 border border-white">
                    <CheckCircle2 className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                </div>
            )}
            {(data?.executionStatus === 'FAILED' || data?.executionStatus === 'Failed') && (
                <div className="absolute -top-2 -right-2 p-1 bg-red-600 rounded-full shadow-md z-20 border border-white">
                    <XCircle className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
                </div>
            )}

            <div className="p-3">
                <Button
                    className={cn(
                        "nodrag w-full h-9 text-xs font-semibold shadow-sm transition-all",
                        "bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 hover:text-indigo-800 hover:border-indigo-300"
                    )}
                    onClick={handleRunProfile}
                    onMouseDown={(e) => e.stopPropagation()}
                    disabled={loading}
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : <BarChart className="w-3 h-3 mr-2" />}
                    {loading ? "Profiling..." : "View Profile"}
                </Button>
            </div>

            {/* Source Handle (Output) */}
            <Handle
                type="source"
                position={Position.Right}
                id="out"
                isConnectable={isConnectable}
                className="w-3 h-3 bg-indigo-500 border-2 border-white z-50"
            />

            {reportData && (
                <DataProfileReport
                    open={open}
                    onOpenChange={setOpen}
                    data={reportData}
                />
            )}
        </div>
    );
});